import { ChangeEvent, Dispatch, KeyboardEvent, SetStateAction, useEffect, useRef, useState } from "react";
import { Filter } from "../../types";
import FilterBox from "./FilterBox";
import { createStylesheet } from "../../../_app/utils/styles";
import { UIAlert, UIChip, UIIconButton, UITextField } from "../../../_app/components";
import { FilterAltOff as FilterAltOffIcon } from "../../../_app/components/icons";

interface Props {
  item: Filter;
  selection: string[];
  setSelection: Dispatch<SetStateAction<string[]>>;
  addSelection: (value?: string | string[]) => void;
  resetSelection?: () => void;
  close: () => void;
}

export default function TextFilter({ item, selection, setSelection, addSelection, resetSelection, close, ...props }: Props) {
  const classes = useStyles();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputValue, setInputValue] = useState("");
  const normalizedSelection = selection ?? [];
  const trimmedInputValue = inputValue.trim();
  const hasSelection = normalizedSelection.length > 0;
  const canApply = Boolean(hasSelection || trimmedInputValue.length > 0);
  const selectionForApply = trimmedInputValue ? [...normalizedSelection, trimmedInputValue] : normalizedSelection;
  const handleReset = () => {
    setInputValue("");
    resetSelection?.();
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const commitPendingInput = () => {
    if (!trimmedInputValue) return normalizedSelection;
    const nextSelection = [...normalizedSelection, trimmedInputValue];
    setSelection(nextSelection);
    setInputValue("");
    return nextSelection;
  };

  const handleApply = () => {
    const nextSelection = trimmedInputValue ? commitPendingInput() : normalizedSelection;
    addSelection(nextSelection);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    commitPendingInput();
  };

  const handleChipDelete = (index: number) => {
    const nextSelection = normalizedSelection.filter((_, currentIndex: number) => currentIndex !== index);
    setSelection(nextSelection);
    if (!nextSelection.length) handleReset();
  };

  return (
    <div {...props}>
      <FilterBox
        item={item}
        resetSelection={handleReset}
        addSelection={handleApply}
        selection={selectionForApply}
        close={close}
        disableApply={!canApply}
      >
        {item.type === "TEXT_CASE_SENSITIVE" && (
          <div className={classes.filterBody}>
            <UIAlert severity="warning">
              This filter is <b>case-sensitive</b>.<br />
              Please match <b>uppercase</b> and <b>lowercase</b> letters exactly.
            </UIAlert>
          </div>
        )}
        <div className={classes.filterBody}>
          {!hasSelection && <div className={classes.helperText}>Press Enter after each value to add multiple filters.</div>}
          <div className={classes.chipContainerWrapper}>
            <div className={classes.chipContainer}>
              {normalizedSelection.map((option: string, index: number) => (
                <UIChip
                  key={`${option}-${index}`}
                  label={option}
                  onDelete={() => handleChipDelete(index)}
                  size="small"
                  variant="outlined"
                  className={classes.chip}
                />
              ))}
            </div>
            {hasSelection && (
              <UIIconButton size="small" onClick={handleReset} className={classes.clearAllButton} title="Clear all">
                <FilterAltOffIcon fontSize="small" />
              </UIIconButton>
            )}
          </div>
          <UITextField
            inputRef={inputRef}
            variant="outlined"
            size="small"
            placeholder="Type value and press Enter"
            fullWidth
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            InputLabelProps={{ shrink: false }}
          />
        </div>
      </FilterBox>
    </div>
  );
}

const useStyles = createStylesheet((theme) => ({
  filterBody: {
    paddingInline: theme.spacing(2),
    marginBottom: theme.spacing(1),
    minWidth: 0,
  },
  chipContainerWrapper: {
    position: "relative",
    marginBottom: theme.spacing(1),
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    maxWidth: "600px",
    paddingRight: theme.spacing(4),
  },
  chip: {
    maxWidth: "300px",
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  clearAllButton: {
    position: "absolute",
    top: 0,
    right: 0,
    padding: theme.spacing(0.5),
  },
  helperText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
}));
